/* eslint-disable max-lines */
import store from 'store';
import {
  OFFER_INIT_GUID,
  OFFER_INIT_WITH_IDOFFER_PARTNERID,
  OFFER_DOCUMENTS_REQUIRED_BY_GUID,
  OFFER_OPERATION_BY_GUID,
  PRECONTRACT_BY_GUID,
  OFFER_COMPLETED,
  OFFER_COMPLETED_KO,
  OFFER_COMPONENTS_BY_PARTNER_ID,
  OFFER_LEGAL_CONDITIONS,
  OFFER_LEGAL_CONDITIONS_BY_GUID,
  STRIPE_PAYMENT_CHECK,
  OFFER_CAMPAIGN_BY_GUID
} from 'store/actions/offer';
import { ONBOARDING_LEGAL_CONDITIONS } from 'store/actions/onboarding';
import { VALIDATION_STOCK } from 'store/actions/operation';
import { UI_GET_BASICS_BY_PARTNERID, SET_UI_PROPERTY } from 'store/actions/ui';

const Layout = () => import(/* webpackChunkName: "pages/main" */ 'layout');
const Error = () => import(/* webpackChunkName: "pages/views/error" */ 'views/Error');
const Control = () => import(/* webpackChunkName: "pages/views/control" */ 'views/Control');
const Legal = () => import(/* webpackChunkName: "pages/views/legal" */ 'views/Legal');
const ContractStep1Request = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-1-request" */ 'views/Contract/Step-1/Request'
  );
const ContractStep1Identity = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-1-identity" */ 'views/Contract/Step-1/Identity'
  );
const ContractStep2 = () =>
  import(/* webpackChunkName: "pages/views/contract-step-2" */ 'views/Contract/Step-2');
const ContractStep3RiskText = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-3-risk-text" */ 'views/Contract/Step-3/RiskText'
  );
const ContractStep3RiskOk = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-3-risk-ok" */ 'views/Contract/Step-3/RiskOk'
  );
const ContractStep3RiskDocuments = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-3-risk-documents" */ 'views/Contract/Step-3/RiskDocuments'
  );
const ContractStep3Ko = () =>
  import(/* webpackChunkName: "pages/views/contract-step-3-ko" */ 'views/Contract/Step-3/Ko');
const ContractStep4 = () =>
  import(/* webpackChunkName: "pages/views/contract-step-4" */ 'views/Contract/Step-4');
const ContractStep5Ok = () =>
  import(/* webpackChunkName: "pages/views/contract-step-5-ok" */ 'views/Contract/Step-5/Ok');
const ContractStep5Ko = () =>
  import(/* webpackChunkName: "pages/views/contract-step-5-ko" */ 'views/Contract/Step-5/Ko');
const ContractStep5StripeRetry = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-5-stripe-retry" */ 'views/Contract/Step-5/StripeRetry'
  );
const OnboardingLogin = () =>
  import(/* webpackChunkName: "pages/views/onboarding-login" */ 'views/Onboarding/Login');
const OnboardingUploadDocumentation = () =>
  import(
    /* webpackChunkName: "pages/views/onboarding-upload-documentation" */ 'views/Onboarding/UploadDocumentation'
  );
const OnboardingThanks = () =>
  import(/* webpackChunkName: "pages/views/onboarding-thanks" */ 'views/Onboarding/Thanks');

// Redirect to the error page
const redirectToErrorPage = (next, resource) => next({ name: 'error', params: { resource } });
// Redirect to the 404 page
const redirectTo404Page = (next, path) => next({ name: '404', params: { 0: path } });
// Redirect to name router
const redirectToRuterName = (data, next, toRouterName = true) => {
  if (toRouterName && data && data.routerName !== 'redirect') {
    next({ name: data.routerName });
  } else if (toRouterName && data && data.url) {
    window.location.href = data.url;
  } else {
    next();
  }
};
// Get basics data by partner id
const basicsDataByPartnerId = (next, partnerId) => {
  store
    .dispatch(`ui/${UI_GET_BASICS_BY_PARTNERID}`, partnerId)
    .then(() => next())
    .catch(() => next());
};

const validationStock = next => {
  store
    .dispatch(`operation/${VALIDATION_STOCK}`)
    .then(data => {
      if (data.status === 'OK') {
        return true;
      }
      store.dispatch(`ui/${SET_UI_PROPERTY}`, {
        field: 'controlTitle',
        value: data.title
      });
      store.dispatch(`ui/${SET_UI_PROPERTY}`, {
        field: 'controlMessage',
        value: data.message
      });
      next({ name: 'control' });
    })
    .catch(() => {
      redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
    });
};

const isContract = partnerId => {
  const partnersNoContract = JSON.parse(process.env.PARTNERS_NO_CONTRACT);
  if (partnersNoContract[partnerId]) {
    const { contract } = partnersNoContract[partnerId];
    if (contract !== undefined) {
      return contract;
    }
  }
  return true;
};

export default [
  {
    path: '/:idOffer(\\d+)/:partnerId(\\d+)/:idc(\\d+)?/:trackerClickId?',
    component: Layout,
    children: [
      {
        path: '/',
        component: ContractStep1Request,
        meta: { titleKey: 'step1Request' },
        name: 'step-1-request',
        beforeEnter(to, from, next) {
          const { params, query } = to;
          if (!isContract(params.partnerId)) return redirectTo404Page(next, to.path);
          store
            .dispatch(`offer/${OFFER_INIT_WITH_IDOFFER_PARTNERID}`, {
              params,
              query
            })
            .then(resp => {
              Promise.all([
                store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-1' }),
                store.dispatch(`offer/${OFFER_LEGAL_CONDITIONS}`, '1')
              ])
                .then(() => redirectToRuterName(resp, next))
                .catch(() => {
                  redirectToErrorPage(next, OFFER_INIT_WITH_IDOFFER_PARTNERID);
                });
            })
            .catch(() => {
              redirectToErrorPage(next, OFFER_INIT_WITH_IDOFFER_PARTNERID);
            });
        }
      }
    ]
  },
  {
    path: '/:guid([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})',
    component: Layout,
    children: [
      {
        path: '/',
        component: ContractStep1Identity,
        meta: { titleKey: 'step1Identity' },
        name: 'step-1-guid',
        beforeEnter(to, from, next) {
          store
            .dispatch(`offer/${OFFER_INIT_GUID}`, to.params)
            .then(resp => {
              Promise.all([
                store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-login' }),
                store.dispatch(`offer/${OFFER_LEGAL_CONDITIONS_BY_GUID}`, '1')
              ])
                .then(() => redirectToRuterName(resp, next))
                .catch(() => {
                  redirectToErrorPage(next, OFFER_INIT_GUID);
                });
            })
            .catch(() => {
              redirectToErrorPage(next, OFFER_INIT_GUID);
            });
        }
      }
    ]
  },
  {
    path: '/contract',
    component: Layout,
    children: [
      {
        path: '/',
        beforeEnter(to, from, next) {
          redirectTo404Page(next, to.path);
        }
      },
      {
        path: 'step-2',
        component: ContractStep2,
        name: 'step-2',
        meta: { titleKey: 'step2', step: 2 },
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            store
              .dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'risk' })
              .then(() => next())
              .catch(() => {
                redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
              });
          }
        }
      },
      {
        path: 'step-3-ok',
        component: ContractStep3RiskOk,
        name: 'step-3-ok',
        meta: { titleKey: 'step3', step: 3 },
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            Promise.all([
              store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-3-ok' }),
              store.dispatch(`offer/${OFFER_DOCUMENTS_REQUIRED_BY_GUID}`, '5A')
            ])
              .then(() => next())
              .catch(() => {
                redirectToErrorPage(next, OFFER_DOCUMENTS_REQUIRED_BY_GUID);
              });
          }
        }
      },
      {
        path: 'step-3-ko',
        component: ContractStep3RiskText,
        name: 'step-3-ko',
        meta: { titleKey: 'step3', step: 0 },
        props: { stepName: 'paso-3-ko' },
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            store
              .dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-3-ko' })
              .then(() => next())
              .catch(() => {
                redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
              });
          }
        }
      },
      {
        path: 'step-3-study',
        component: ContractStep3RiskDocuments,
        name: 'step-3-study',
        meta: { titleKey: 'step3', step: 0 },
        props: { stepName: 'paso-3-study' },
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            Promise.all([
              store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-3-study' }),
              store.dispatch(`offer/${OFFER_DOCUMENTS_REQUIRED_BY_GUID}`, '5C'),
              store.dispatch(`offer/${OFFER_LEGAL_CONDITIONS}`, '5C')
            ])
              .then(() => next())
              .catch(() => {
                redirectToErrorPage(next, OFFER_DOCUMENTS_REQUIRED_BY_GUID);
              });
          }
        }
      },
      {
        path: 'step-3-fc',
        component: ContractStep3RiskDocuments,
        name: 'step-3-fc',
        meta: { titleKey: 'step3', step: 3 },
        props: { stepName: 'paso-3-fc' },
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            Promise.all([
              store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-3-fc' }),
              store.dispatch(`offer/${OFFER_DOCUMENTS_REQUIRED_BY_GUID}`, '5D'),
              store.dispatch(`offer/${OFFER_LEGAL_CONDITIONS}`, '5D')
            ])
              .then(() => next())
              .catch(() => {
                redirectToErrorPage(next, OFFER_DOCUMENTS_REQUIRED_BY_GUID);
              });
          }
        }
      },
      {
        path: 'step-3-pending',
        component: ContractStep3RiskText,
        name: 'step-3-pending',
        meta: { titleKey: 'step3', step: 0 },
        props: { stepName: 'paso-3-pending' },
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            store
              .dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-3-pending' })
              .then(() => next())
              .catch(() => {
                redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
              });
          }
        }
      },
      {
        path:
          'step-3/stripe-check/:guid([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})?',
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            const { params, query } = to;
            Promise.all([
              store.dispatch(`offer/${STRIPE_PAYMENT_CHECK}`, { params, query }),
              store.dispatch(`offer/${OFFER_CAMPAIGN_BY_GUID}`, params.guid)
            ])
              .then(resp => {
                next({ name: resp[0].routerName });
              })
              .catch(() => {
                redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
              });
          }
        }
      },
      {
        path: 'card-retry',
        component: ContractStep5StripeRetry,
        name: 'step-3-stripe-retry',
        meta: { titleKey: 'step3', step: 3 },
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            store
              .dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-5-tarjeta-nueva' })
              .then(() => next())
              .catch(() => {
                redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
              });
          }
        }
      },
      {
        path: 'ko',
        component: ContractStep3Ko,
        name: 'step-3-stripe-ko',
        meta: { titleKey: 'step3' },
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            store
              .dispatch(`offer/${OFFER_COMPLETED_KO}`)
              .then(resp => {
                if (resp && resp === '404') {
                  redirectTo404Page(next, to.path);
                } else {
                  next();
                }
              })
              .catch(() => {
                redirectToErrorPage(next, OFFER_COMPLETED_KO);
              });
          }
        }
      },
      {
        path: 'step-4',
        component: ContractStep4,
        name: 'step-4',
        meta: { titleKey: 'step4', step: 4 },
        beforeEnter(to, from, next) {
          const validation = Promise.all([validationStock(next)]);
          if (validation) {
            store
              .dispatch(`offer/${PRECONTRACT_BY_GUID}`)
              .then(() => {
                store
                  .dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-4' })
                  .then(() => {
                    // If it comes from step-3 then call operations endpoint
                    if ((from && from.name && from.name.indexOf('step-3') === 0) || !from.name) {
                      store
                        .dispatch(`offer/${OFFER_OPERATION_BY_GUID}`)
                        .then(() => next())
                        .catch(() => {
                          redirectToErrorPage(next, OFFER_OPERATION_BY_GUID);
                        });
                    } else {
                      next();
                    }
                  })
                  .catch(() => {
                    redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
                  });
              })
              .catch(() => {
                redirectToErrorPage(next, PRECONTRACT_BY_GUID);
              });
          }
        }
      }
    ]
  },
  {
    path: '/completed',
    component: Layout,
    children: [
      {
        path: '/',
        beforeEnter(to, from, next) {
          redirectTo404Page(next, to.path);
        }
      },
      {
        path: ':guid([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})?/ok',
        component: ContractStep5Ok,
        name: 'step-5-ok',
        meta: { titleKey: 'step5Ok' },
        beforeEnter(to, from, next) {
          store
            .dispatch(`offer/${OFFER_COMPLETED}`, {
              params: to.params,
              query: to.query
            })
            .then(resp => {
              store
                .dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'general' })
                .then(() => {
                  redirectToRuterName(resp, next, resp && resp.routerName !== 'step-5-ok');
                })
                .catch(() => {
                  redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
                });
            })
            .catch(() => {
              redirectToErrorPage(next, OFFER_COMPLETED);
            });
        }
      },
      {
        path: 'card-retry',
        component: ContractStep5StripeRetry,
        name: 'step-5-stripe-retry',
        meta: { titleKey: 'step5StripeRetry', step: 5 },
        beforeEnter(to, from, next) {
          store
            .dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'paso-5-tarjeta-nueva' })
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
            });
        }
      },
      {
        path: 'direct/ok',
        component: ContractStep5Ok,
        name: 'step-5-direct-ok',
        meta: { titleKey: 'step5Ok', step: 6 },
        beforeEnter(to, from, next) {
          store
            .dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, { id: 'general' })
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
            });
        }
      },
      {
        path: 'ko',
        component: ContractStep5Ko,
        name: 'step-5-ko',
        meta: { titleKey: 'step5Ko' },
        beforeEnter(to, from, next) {
          store
            .dispatch(`offer/${OFFER_COMPLETED_KO}`)
            .then(resp => {
              if (resp && resp === '404') {
                redirectTo404Page(next, to.path);
              } else {
                next();
              }
            })
            .catch(() => {
              redirectToErrorPage(next, OFFER_COMPLETED_KO);
            });
        }
      }
    ]
  },
  {
    path: '/onboarding',
    component: Layout,
    props: { whithoutSubHeader: true },
    children: [
      {
        path: '/',
        beforeEnter(to, from, next) {
          redirectTo404Page(next, to.path);
        }
      },
      {
        path:
          ':operationGuid([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/login',
        component: OnboardingLogin,
        meta: { titleKey: 'onboarding-login' },
        name: 'onboarding-login',
        beforeEnter(to, from, next) {
          Promise.all([
            store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, {
              id: 'onboarding-login',
              partnerId: process.env.ONBOARDING_PARTNER
            }),
            store.dispatch(`onboarding/${ONBOARDING_LEGAL_CONDITIONS}`, {
              step: 'onboarding'
            })
          ])
            .then(() => {
              next();
            })
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path:
          ':operationGuid([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/uploadDocumentation',
        component: OnboardingUploadDocumentation,
        meta: { titleKey: 'onboarding-upload-documentation' },
        name: 'onboarding-upload-documentation',
        beforeEnter(to, from, next) {
          Promise.all([
            store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, {
              id: 'onboarding-upload-documentation',
              partnerId: process.env.ONBOARDING_PARTNER
            })
          ])
            .then(() => {
              next();
            })
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path:
          ':operationGuid([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/thanks',
        component: OnboardingThanks,
        meta: { titleKey: 'onboarding-thanks' },
        name: 'onboarding-thanks',
        beforeEnter(to, from, next) {
          Promise.all([
            store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, {
              id: 'onboarding-thanks',
              partnerId: process.env.ONBOARDING_PARTNER
            })
          ])
            .then(() => {
              next();
            })
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      }
    ]
  },
  {
    path: '/',
    component: Layout,
    props: { whithoutSubHeader: true },
    children: [
      {
        path: 'legal/:type/:partnerId(\\d+)?',
        component: Legal,
        name: 'legal',
        props: { whithoutLinksFooter: true },
        meta: { titleKey: 'legal' },
        beforeEnter: (to, from, next) => {
          const { partnerId } = to.params;
          basicsDataByPartnerId(next, partnerId);
        }
      },
      {
        path: 'error',
        name: 'error',
        component: Error,
        props: { type: '500_es-ES' },
        meta: { titleKey: 'error' },
        beforeEnter: (to, from, next) => {
          basicsDataByPartnerId(next);
        }
      },
      {
        path: 'control',
        name: 'control',
        component: Control,
        meta: { titleKey: 'control' },
        beforeEnter: (to, from, next) => {
          basicsDataByPartnerId(next);
        }
      },
      {
        path: '*',
        name: '404',
        component: Error,
        props: { type: '404_es-ES' },
        meta: { titleKey: 'notFound' },
        beforeEnter: (to, from, next) => {
          basicsDataByPartnerId(next);
        }
      }
    ]
  }
];
